/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { api, API_BASE_URL, fetchBounded } from '../services/api'
import { clearPendingPlan } from '../lib/pendingPlan'

interface AuthUser {
    reviewer?: boolean
    reviewClassIds?: string[]
    authenticated: boolean
    id: number
    email: string
    firstName: string
    lastName: string
    role: string
    provider: string
    enabled: boolean
    // Email confirmed. false right after signup ("session now, verify later") — drives the soft
    // "verify your email" nudge; no feature is gated on it.
    emailVerified?: boolean
    createdAt: string
    updatedAt: string
    hasActiveSubscription: boolean
    subscriptionExpiresAt: string | null
    subscriptionTier: 'FREE' | 'FULL_ACCESS'
    name?: string
    picture?: string
    avatar?: string
    hasPassword?: boolean
    preferredLanguage?: string
}

interface AuthContextType {
    user: AuthUser | null
    isLoading: boolean
    /**
     * True once the initial auth check is complete.
     * Use this as a guard before firing authenticated API calls.
     * AdminDashboard, Dashboard, etc. should wait for ready === true.
     */
    ready: boolean
    /** The profile check could not reach the server; the session itself is kept. */
    authOffline: boolean
    login: (email: string, password: string) => Promise<AuthUser | null>
    register: (name: string, email: string, password: string, ref?: string, language?: string, phoneNumber?: string, whatsappOptIn?: boolean) => Promise<AuthUser | null>
    googleLogin: () => void
    facebookLogin: () => void
    completeOAuthLogin: (token: string, expiresIn: number) => Promise<AuthUser | null>
    logout: () => Promise<void>
    checkAuth: () => Promise<AuthUser | null>
    // Password reset
    forgotPassword: (email: string) => Promise<void>
    resetPassword: (token: string, password: string) => Promise<void>
    // Email verification
    verifyEmail: (token: string) => Promise<void>
    resendVerification: (email: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    /**
     * ready: false until the very first auth initialization is complete.
     * This is the key flag that prevents 403s on admin pages — they must
     * wait for ready === true before firing any authenticated API call.
     */
    const [ready, setReady] = useState(false)
    // True when the last /auth/me could not reach the server (offline or timed out).
    const [authOffline, setAuthOffline] = useState(false)

    const clearAuthData = useCallback(() => {
        setUser(null)
        // A cleared session is signed out, not offline: protected routes must send it to /login.
        setAuthOffline(false)
        api.clearTokens()
    }, [])

    const checkAuth = useCallback(async (): Promise<AuthUser | null> => {
        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            }
            const token = api.getAccessToken()
            if (token) {
                headers['Authorization'] = `Bearer ${token}`
            }

            const userData = await fetchBounded(`${API_BASE_URL}/auth/me`, {
                method: 'GET',
                credentials: 'include',
                headers,
            }, response => {
                // A server outage (5xx) or rate limit (429) says nothing about the session:
                // never read it as signed out.
                if (response.status >= 500 || response.status === 429) {
                    const err: Error & { status?: number } = new Error(`Request failed with status ${response.status}`)
                    err.name = 'ServerError'
                    err.status = response.status
                    throw err
                }
                return response.json()
            })
            setAuthOffline(false)

            if (userData.authenticated) {
                const picture = userData.picture || userData.avatar
                const newUser: AuthUser = {
                    ...userData,
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    role: userData.role || 'STUDENT',
                    picture: picture || undefined,
                    avatar: picture || undefined,
                    name:
                        userData.name ||
                        `${userData.firstName || ''} ${userData.lastName || ''}`.trim() ||
                        userData.email,
                }
                setUser(newUser)
                return newUser
            }

            clearAuthData()
            return null
        } catch (err) {
            // A dropped connection, the 15 s bound or a 5xx says nothing about the session:
            // keep the tokens and any user, and let guards show the offline state.
            if (err instanceof Error && (err.name === 'NetworkError' || err.name === 'ServerError')) {
                setAuthOffline(true)
                return null
            }
            clearAuthData()
            return null
        }
    }, [clearAuthData])

    const login = async (email: string, password: string): Promise<AuthUser | null> => {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
            credentials: 'include',
        })

        const data = await response.json()

        if (!response.ok) {
            throw new Error(data.error || 'Login failed')
        }

        if (data.accessToken) {
            api.setAccessToken(data.accessToken, data.expiresIn)
        }
        // Refresh token is now stored as an HttpOnly cookie by the backend.
        // We do not store it in localStorage — the cookie travels automatically
        // via credentials: 'include' on /auth/refresh calls.

        await new Promise(resolve => setTimeout(resolve, 50))
        const loggedUser = await checkAuth()
        localStorage.setItem('dhtn-has-session', '1')
        return loggedUser
    }

    const register = async (name: string, email: string, password: string, ref?: string, language?: string, phoneNumber?: string, whatsappOptIn?: boolean) => {
        const [firstName, ...rest] = name.split(' ')
        const lastName = rest.join(' ') || ''

        const body: Record<string, string> = { firstName, lastName, email, password, language: language || 'ha' }
        if (ref) body.ref = ref
        if (phoneNumber) body.phoneNumber = phoneNumber
        if (whatsappOptIn !== undefined) body.whatsappOptIn = String(whatsappOptIn)

        const response = await fetch(`${API_BASE_URL}/auth/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            credentials: 'include',
        })

        const data = await response.json()

        if (!response.ok) {
            throw new Error(data.error || 'Registration failed')
        }

        // Session now, verify later: signup returns a session just like login, so the new account
        // is logged in immediately (onboarding continues seamlessly). Email verification is a soft
        // nudge (user.emailVerified stays false), not a gate.
        if (data.accessToken) {
            api.setAccessToken(data.accessToken, data.expiresIn)
            await new Promise(resolve => setTimeout(resolve, 50))
            const registeredUser = await checkAuth()
            localStorage.setItem('dhtn-has-session', '1')
            return registeredUser
        }
        return null
    }

    // Password reset
    const forgotPassword = async (email: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
            credentials: 'include',
        })

        if (!response.ok) throw new Error('Failed to send reset email')
    }

    const resetPassword = async (token: string, password: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password }),
            credentials: 'include',
        })
        if (!response.ok) {
            const data = await response.json().catch(() => ({}))
            throw new Error(data.error || 'Failed to reset password')
        }
    }

    // Email verification
    const verifyEmail = async (token: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
            credentials: 'include',
        })
        if (!response.ok) {
            const data = await response.json().catch(() => ({}))
            throw new Error(data.error || 'Failed to verify email')
        }
    }

    const resendVerification = async (email: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
            credentials: 'include',
        })

        if (!response.ok) throw new Error('Failed to resend verification')
    }

    const googleLogin = () => {
        clearAuthData()
        window.location.href = `${API_BASE_URL}/oauth2/authorization/google`
    }

    const facebookLogin = () => {
        clearAuthData()
        window.location.href = `${API_BASE_URL}/oauth2/authorization/facebook`
    }

    const completeOAuthLogin = async (token: string, expiresIn: number): Promise<AuthUser | null> => {
        api.setAccessToken(token, expiresIn)
        const loggedUser = await checkAuth()
        localStorage.setItem('dhtn-has-session', '1')
        return loggedUser
    }

    const logout = async () => {
        try {
            const refreshToken = api.getRefreshToken()
            await fetch(`${API_BASE_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: refreshToken || undefined }),
            })
        } catch {
            // Proceed with local cleanup even if server call fails
        } finally {
            clearAuthData()
            localStorage.removeItem('dhtn-has-session')
            clearPendingPlan()
            // to the landing page, where the Living mark asks them to stay (founder, 15 Oct 2026); the flag only
            // says "just logged out", nothing about who, and LivingHero clears it once read
            try { sessionStorage.setItem('dhtn-just-left', '1') } catch { /* storage blocked: a normal greeting */ }
            window.location.href = '/'
        }
    }

    /**
     * initAuth - runs once on mount.
     *
     * Flow:
     * 1. Try to get a fresh access token via refresh cookie.
     * 2. If refresh succeeds → call /auth/me to hydrate user.
     * 3. If refresh fails → user is not logged in (stay on login page).
     * 4. In ALL cases: set ready = true so guarded pages can proceed.
     *
     * Why this fixes the flash:
     * - isLoading stays true until initAuth fully completes.
     * - ready stays false until initAuth fully completes.
     * - Protected route guards render a loader while !ready.
     * - They only redirect to /login when ready === true && user === null.
     * - No more premature redirect → no flash.
     */
    useEffect(() => {
        const initAuth = async () => {
            setIsLoading(true)
            try {
                // Fast-path: if no session hint exists, the user has never logged in
                // (or explicitly logged out). Skip the network call entirely.
                const hasSession = localStorage.getItem('dhtn-has-session') === '1'
                if (!hasSession) {
                    clearAuthData()
                    return
                }
                // Restore session via refresh token (HttpOnly cookie — sent automatically)
                const refreshed = await api.refreshAccessToken()
                if (refreshed) {
                    await checkAuth()
                } else {
                    // Token is expired or invalid — clear auth state.
                    // We intentionally keep 'dhtn-has-session' here: refreshAccessToken()
                    // returns false only for a real 401/403 (an outage throws and lands in
                    // the catch below). The hint is cleared only on explicit logout().
                    clearAuthData()
                }
            } catch {
                // An outage during the refresh (ServerError / NetworkError) or checkAuth
                // throwing: this visit starts signed out, but the session hint is kept, so
                // the next visit tries the refresh again.
                clearAuthData()
            } finally {
                setIsLoading(false)
                setReady(true)
            }
        }

        initAuth()
    }, [checkAuth, clearAuthData])

    // A deep API call elsewhere in the app can discover the session is genuinely
    // expired (refresh token invalid) long after initAuth ran. Without this, `user`
    // stays stale/truthy — ProtectedRoute would keep rendering protected pages on a
    // dead session. api.ts dispatches this only for a confirmed-invalid refresh,
    // never for a transient network failure.
    useEffect(() => {
        const onAuthExpired = () => clearAuthData()
        window.addEventListener('dhtn:auth-expired', onAuthExpired)
        return () => window.removeEventListener('dhtn:auth-expired', onAuthExpired)
    }, [clearAuthData])

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoading,
                ready,
                authOffline,
                login,
                register,
                googleLogin,
                facebookLogin,
                completeOAuthLogin,
                logout,
                checkAuth,
                forgotPassword,
                resetPassword,
                verifyEmail,
                resendVerification,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used within AuthProvider')
    return ctx
}
